import { Box, Dialog, Typography } from '@mui/material'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Context from '../Context'
import theme from '../theme'
import { useFeed, useBoardPrices, FONT } from '../components/board'
import PriceHistoryModal from '../components/PriceHistoryModal'
import LadderCard, { markSx } from '../components/LadderCard'
import { historyRequestFor } from '../components/Ladders'

/**
 * The starred games as small cards rather than one long table, several to a row, so a morning's worth of
 * fixtures is on one screen instead of a scroll.
 *
 * A card can be dragged anywhere on the board and it stays there: the two games somebody is actually
 * watching go side by side at the top whatever their kick off times say, and a card can be laid over
 * another one if that is what its owner wants. Cards nobody has moved keep flowing in the chosen order
 * behind the ones that have been placed, and the whole arrangement belongs to the account.
 *
 * The prices come from the market snapshot rather than the comparison feed, because that is where the
 * whole ladder lives — the team totals with it, which the feed does not carry at all. Only the main line
 * of each market is drawn, laid out as the snapshot lays it out: one column per market, the line beside
 * the price it belongs to.
 *
 * Pinnacle alone: the question this screen answers is "where is the market", and our own price beside it
 * would double the height of every card for a number the trader already knows.
 */

/**
 * Where this screen's arrangement is kept: in the browser, under the name of the account signed in, not
 * in the account's settings on the server.
 *
 * Two reasons. Dragging writes a position on every mouse movement, and every settings write posts the
 * whole settings object, so a board being tidied would be firing whole-document writes at the account
 * several times a second — and a second tab of the same account, holding an older copy, would overwrite
 * them all on its next save. And where the cards sit on one screen is a fact about that screen: a laptop
 * and a wall display do not want the same arrangement.
 *
 * Per account name, because two people sharing a browser should not inherit each other's board.
 */
// a constant rather than an inline object: a new object every render would refetch on every render
const FAVOURITES_ONLY = { favOnly: 'true' }

const STORE = 'oddspulse:favorites-board'

const readLayout = (username) => {
  try {
    const raw = localStorage.getItem(`${STORE}:${username || 'demo'}`)
    const saved = raw ? JSON.parse(raw) : null
    return saved && typeof saved === 'object' ? saved : {}
  } catch (e) {
    return {}
  }
}

const writeLayout = (username, value) => {
  try {
    localStorage.setItem(`${STORE}:${username || 'demo'}`, JSON.stringify(value))
  } catch (e) {}
}
const BOOK = 'Pinnacle'

const LINE_CHOICES = [1, 2, 3, 5]
const SORTS = [
  { key: 'ko', label: 'Kick off' },
  { key: 'league', label: 'League' }
]

// how far the mouse has to travel before a press on a card counts as carrying it somewhere
const DRAG_SLOP = 4

// the same gap the flow layout uses, so a card dropped into a hole lines up with the ones already there
const CARD_GAP = 10

/**
 * How long a starred game stays on the board after the feed stops mentioning it. The comparison feed
 * answers from more than one cache and occasionally returns a poll without a fixture that is plainly
 * still there — a card that came and went every twenty seconds is that, not a game being unstarred.
 * A game the feed says is no longer starred goes at once; this is only for silence.
 */
const MARKETS = [
  { key: 'ml', label: '1X2' },
  { key: 'spread', label: 'AH' },
  { key: 'total', label: 'AOU' },
  { key: 'tt', label: 'Team totals' }
]
const ALL_MARKETS = MARKETS.map((market) => market.key)

const bookSx = { ...markSx, textTransform: 'uppercase', letterSpacing: '0.4px' }

const chip = (active) => ({
  appearance: 'none',
  height: '26px',
  padding: '0 10px',
  border: `1px solid ${active ? theme.palette.primary.light : '#D6D6DC'}`,
  backgroundColor: active ? theme.palette.primary.light : 'white',
  color: active ? 'white' : '#6E7378',
  borderRadius: '2px',
  fontFamily: FONT,
  fontSize: '12px',
  cursor: 'pointer'
})

const keyOf = (event) => `${event.home}|${event.away}|${event.koTime}`

function FavoritesBoardPage({ pollingInterval }) {
  const { usPrices, username, setIsSideBarOpen } = useContext(Context)
  // nothing is written until something on this screen is actually changed by hand
  const touched = useRef(false)

  const [ lines, setLines ] = useState(1)
  const [ markets, setMarkets ] = useState(ALL_MARKETS)
  const [ sort, setSort ] = useState('ko')
  // where cards have been carried to, by fixture: {x, y, z}. Anything not in here is still in the flow
  const [ places, setPlaces ] = useState({})
  const [ tuning, setTuning ] = useState(false)

  // the cards want the width; the menu is navigation nobody uses while reading a board
  useEffect(() => {
    setIsSideBarOpen(false)
    // eslint-disable-next-line
  }, [])

  /**
   * The arrangement is read when the account is known, not on the first render: who is signed in arrives
   * a moment after the screen does, and a board that read too early would restore whatever the last
   * account left in this browser — or nothing, and then save its emptiness over what was there.
   */
  useEffect(() => {
    if (!username || touched.current) {
      return
    }
    const saved = readLayout(username)
    setLines(LINE_CHOICES.includes(saved.lines) ? saved.lines : 1)
    setMarkets(Array.isArray(saved.markets) ? saved.markets : ALL_MARKETS)
    setSort(SORTS.some((one) => one.key === saved.sort) ? saved.sort : 'ko')
    setPlaces(saved.places && typeof saved.places === 'object' ? saved.places : {})
    // eslint-disable-next-line
  }, [username])

  // the feed is what knows which games are starred; the prices come from the snapshot below
  // the starred games and nothing else: the server decides what is on this board, the same way it does
  // on every other screen
  const { events, error } = useFeed(pollingInterval, FAVOURITES_ONLY)

  // the arrangement, written where it is read from. localStorage is synchronous and local, so there is
  // nothing to debounce and nothing to flush on the way out
  const chosen = useMemo(() => ({ sort, places, lines, markets }), [sort, places, lines, markets])

  useEffect(() => {
    if (touched.current) {
      writeLayout(username, chosen)
    }
    // eslint-disable-next-line
  }, [chosen])

  const edit = (apply) => (...args) => {
    touched.current = true
    apply(...args)
  }

  const favourites = useMemo(() => {
    const out = []
    const now = Date.now()

    for (const event of events || []) {
      if (!event.fav) {
        continue
      }
      const card = {
        key: keyOf(event),
        home: event.home,
        away: event.away,
        koTime: event.koTime,
        fav: true,
        sport: event.sport,
        league: event.league,
        /**
         * A match under way is still on this board, because it is still on the favourites screen and the
         * two should not disagree about what is starred. What it is not is tradeable: the pre-match book
         * has stopped moving, so the card goes to the back and greys itself out.
         */
        started: Number(event.koTime) <= now
      }
      out.push(card)
    }

    const within = {
      league: (a, b) => `${a.sport}${a.league}`.localeCompare(`${b.sport}${b.league}`) || a.koTime - b.koTime,
      ko: (a, b) => a.koTime - b.koTime || a.home.localeCompare(b.home)
    }[sort] || ((a, b) => a.koTime - b.koTime)

    // what has already started goes behind what has not, whatever the order: it is the least actionable
    out.sort((a, b) => (Boolean(a.started) === Boolean(b.started) ? within(a, b) : (a.started ? 1 : -1)))
    return out
  }, [events, sort])

  // the prices themselves: one request for every starred game, from the snapshot the server holds
  const { games, error: priceError, movedOf } = useBoardPrices(favourites, pollingInterval, BOOK)

  const cards = useMemo(
    () => favourites.map((card) => ({ ...card, game: games ? games.get(card.key) || null : null })),
    [favourites, games]
  )

  /**
   * A card starred after the board was arranged by hand has nowhere to be: every other card is out of the
   * flow, so it would be drawn in the corner underneath one of them. It is dropped into the first gap
   * big enough to hold it instead - left to right, top to bottom, the way one would put it down by hand -
   * and positions belonging to games nobody has starred any more are dropped rather than kept for ever.
   */
  const sizeOf = (key) => {
    const node = nodes.current[key]
    return node && node.offsetWidth
      ? { w: node.offsetWidth, h: node.offsetHeight }
      : { w: 320, h: 150 }
  }

  const freeSpot = (taken, size, width) => {
    const stepX = size.w + CARD_GAP
    const stepY = size.h + CARD_GAP
    const columns = Math.max(1, Math.floor((width + CARD_GAP) / stepX))
    for (let row = 0; row < 400; row++) {
      for (let column = 0; column < columns; column++) {
        const spot = { x: column * stepX, y: row * stepY, w: size.w, h: size.h }
        const clash = taken.some((one) => spot.x < one.x + one.w && one.x < spot.x + spot.w
          && spot.y < one.y + one.h && one.y < spot.y + spot.h)
        if (!clash) {
          return spot
        }
      }
    }
    return { x: 0, y: 0, w: size.w, h: size.h }
  }

  useEffect(() => {
    if (!Object.keys(places).length || !cards.length) {
      return
    }
    const here = new Set(cards.map((card) => card.key))
    const homeless = cards.filter((card) => !places[card.key])
    const stale = Object.keys(places).filter((key) => !here.has(key))
    if (!homeless.length && !stale.length) {
      return
    }
    const width = board.current ? board.current.clientWidth : 0
    setPlaces((previous) => {
      const next = {}
      const taken = []
      for (const [key, place] of Object.entries(previous)) {
        if (here.has(key)) {
          next[key] = place
          const size = sizeOf(key)
          taken.push({ x: place.x, y: place.y, w: size.w, h: size.h })
        }
      }
      for (const card of homeless) {
        const spot = freeSpot(taken, sizeOf(card.key), width)
        next[card.key] = { x: spot.x, y: spot.y, z: 1 }
        taken.push(spot)
      }
      return next
    })
    // eslint-disable-next-line
  }, [cards])

  /**
   * Carrying a card. The press is not a drag until the mouse has actually travelled — otherwise every
   * click on a price would move the card a pixel and count as an arrangement — and once it has, the
   * card leaves the flow and follows the cursor until it is let go. The last card picked up is the one
   * on top, which is what makes laying one over another useful rather than confusing.
   */
  const board = useRef(null)
  const carry = useRef(null)
  const nodes = useRef({})
  const swallow = useRef(false)
  const [ dragging, setDragging ] = useState(null)
  const [ active, setActive ] = useState(null)

  /**
   * The first card to be picked up pins every other card where it already is. Without that, taking one
   * card out of the flow lets the rest re-pack around the hole, and the board rearranges itself under
   * somebody who only wanted to move one thing.
   */
  const pinAll = () => setPlaces((previous) => {
    const area = board.current.getBoundingClientRect()
    const next = { ...previous }
    for (const [key, node] of Object.entries(nodes.current)) {
      if (!node || next[key]) {
        continue
      }
      const box = node.getBoundingClientRect()
      next[key] = { x: Math.max(0, box.left - area.left), y: Math.max(0, box.top - area.top), z: 1 }
    }
    return next
  })

  const startCarry = (event, key) => {
    // only the left button, and never from inside something that answers a click of its own
    if (event.button !== 0) {
      return
    }
    // a click that swallowed nothing must not swallow the next one somewhere else
    swallow.current = false

    /**
     * Touching a card brings it forward and marks it as the one in hand. It is only a z-index change on
     * a card that already has a place: raising one that is still in the flow would mean pinning the
     * whole board, and a plain click is not an arrangement.
     */
    setActive(key)
    setPlaces((previous) => {
      const mine = previous[key]
      if (!mine) {
        return previous
      }
      // the highest of the OTHERS: comparing against a maximum this card is itself part of means a board
      // where everything sits on the same layer never moves, which is exactly the board before any drag
      const top = Math.max(0, ...Object.entries(previous)
        .filter(([other]) => other !== key)
        .map(([, one]) => one.z || 0))
      return (mine.z || 0) > top ? previous : { ...previous, [key]: { ...mine, z: top + 1 } }
    })
    /**
     * Refuse the selection before the browser starts one: a press and a sweep across a card is exactly
     * how text is selected, and half the board comes up blue on the first drag. Clicks are unaffected —
     * they are dispatched separately — so the prices and the star still answer.
     */
    event.preventDefault()
    const card = event.currentTarget.getBoundingClientRect()
    const area = board.current.getBoundingClientRect()
    carry.current = {
      key,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      // where in the card it was grabbed, so it does not jump under the cursor
      offsetX: event.clientX - card.left,
      offsetY: event.clientY - card.top,
      area
    }
  }

  useEffect(() => {
    const move = (event) => {
      const held = carry.current
      if (!held) {
        return
      }
      if (!held.moved) {
        if (Math.abs(event.clientX - held.startX) < DRAG_SLOP && Math.abs(event.clientY - held.startY) < DRAG_SLOP) {
          return
        }
        held.moved = true
        // now that this is a drag, every other card is nailed to where it already is; taking one card
        // out of the flow otherwise lets the rest re-pack around the hole
        pinAll()
        setDragging(held.key)
        // a press and a sweep across a card is how a browser is asked to select text, and it obliges
        // halfway through the first drag; the selection is dropped and the rest are refused
        const selection = window.getSelection()
        if (selection) {
          selection.removeAllRanges()
        }
      }
      event.preventDefault()
      // the board scrolls with the page, so the area is re-read rather than remembered
      const area = board.current.getBoundingClientRect()
      const x = Math.max(0, event.clientX - area.left - held.offsetX)
      const y = Math.max(0, event.clientY - area.top - held.offsetY)
      touched.current = true
      setPlaces((previous) => {
        const top = Math.max(0, ...Object.values(previous).map((one) => one.z || 0)) + 1
        const before = previous[held.key]
        return { ...previous, [held.key]: { x, y, z: before && before.z === top - 1 ? before.z : top } }
      })
    }

    const stop = () => {
      const held = carry.current
      carry.current = null
      if (held && held.moved) {
        // the click that follows a drag is not a click on anything: swallow it once
        swallow.current = true
        setDragging(null)
      }
    }

    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', stop)
    return () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', stop)
    }
  }, [])

  const swallowClick = (event) => {
    if (swallow.current) {
      swallow.current = false
      event.preventDefault()
      event.stopPropagation()
    }
  }

  // which cards have been opened up, by fixture rather than by position, so a card keeps its panel when
  // the board is rebuilt or reordered around it
  const [ opened, setOpened ] = useState(() => new Set())
  const expand = (key) => setOpened((prev) => {
    const next = new Set(prev)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    return next
  })
  const [ historyRequest, setHistoryRequest ] = useState(null)

  return (
    <Box sx={{ padding: '18px 22px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <Box component="button" type="button" onClick={() => setTuning(true)} sx={chip(false)}>
          Display
        </Box>

        <Typography sx={{ ...markSx, marginLeft: 'auto' }}>
          {cards.length} starred · drag a card anywhere
        </Typography>
      </Box>

      {(error || priceError) && (
        <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: theme.palette.secondary.main }}>
          {error || priceError}
        </Typography>
      )}

      {events !== null && cards.length === 0 && (
        <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#9A9EA3' }}>
          Nothing starred yet. The star sits beside every game on the other screens.
        </Typography>
      )}

      {/* placed cards are taken out of the flow and sat where they were left; the rest keep packing
          themselves into the space that is left */}
      <Box
        ref={board}
        onClickCapture={swallowClick}
        sx={{
          position: 'relative',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: '10px',
          userSelect: dragging ? 'none' : 'auto',
          minHeight: `${Math.max(80, ...Object.values(places).map((one) => one.y + 160))}px`
        }}
      >
        {cards.map((card) => (
          <LadderCard
            key={card.key}
            card={card}
            game={card.game}
            loading={games === null}
            book={BOOK}
            markets={markets}
            lines={lines}
            usPrices={usPrices}
            movedOf={(runnerKey) => movedOf(card.key, runnerKey)}
            expanded={opened.has(card.key)}
            onExpand={expand}
            onPrice={(market, line, leg) => setHistoryRequest(historyRequestFor(card, market, line, leg, BOOK))}
            drag={{
              onMouseDown: (event) => startCarry(event, card.key),
              ref: (node) => { nodes.current[card.key] = node }
            }}
            sx={{
              cursor: dragging === card.key ? 'grabbing' : 'grab',
              ...(places[card.key] ? {
                position: 'absolute',
                left: `${places[card.key].x}px`,
                top: `${places[card.key].y}px`,
                zIndex: places[card.key].z || 1
              } : {}),
              ...(active === card.key && dragging !== card.key
                ? { borderColor: theme.palette.primary.light, boxShadow: '0 2px 10px rgba(0, 0, 0, 0.10)' }
                : {}),
              ...(dragging === card.key
                ? { boxShadow: '0 6px 18px rgba(0, 0, 0, 0.16)', borderColor: theme.palette.primary.light, zIndex: 999 }
                : {})
            }}
          />
        ))}
      </Box>

      <Dialog open={tuning} onClose={() => setTuning(false)} maxWidth="xs" fullWidth>
        <Box sx={{ padding: '18px 20px 20px', fontFamily: FONT }}>
          <Typography variant="h4" sx={{ color: theme.palette.primary.main, marginBottom: '16px' }}>Display</Typography>

          {[
            { label: 'Lines per market', choices: LINE_CHOICES, value: lines, set: setLines, text: (one) => (one === 1 ? 'Main only' : one) }
          ].map((row) => (
            <Box key={row.label} sx={{ marginBottom: '14px' }}>
              <Typography sx={{ ...bookSx, marginBottom: '6px' }}>{row.label}</Typography>
              <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {row.choices.map((one) => (
                  <Box key={one} component="button" type="button" onClick={edit(() => row.set(one))} sx={chip(row.value === one)}>
                    {row.text(one)}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}

          <Box sx={{ marginBottom: '14px' }}>
            <Typography sx={{ ...bookSx, marginBottom: '6px' }}>Order</Typography>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {SORTS.map((option) => (
                <Box key={option.key} component="button" type="button" onClick={edit(() => { setSort(option.key); setPlaces({}) })} sx={chip(sort === option.key)}>
                  {option.label}
                </Box>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography sx={{ ...bookSx, marginBottom: '6px' }}>Markets</Typography>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {MARKETS.map((market) => (
                <Box
                  key={market.key}
                  component="button"
                  type="button"
                  // the last market cannot be switched off: a card with no prices on it is not a card
                  onClick={edit(() => setMarkets((current) => (
                    current.includes(market.key)
                      ? (current.length > 1 ? current.filter((key) => key !== market.key) : current)
                      : ALL_MARKETS.filter((key) => key === market.key || current.includes(key))
                  )))}
                  sx={chip(markets.includes(market.key))}
                >
                  {market.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Dialog>

      <PriceHistoryModal request={historyRequest} onClose={() => setHistoryRequest(null)} />
    </Box>
  )
}

export default FavoritesBoardPage
